import { ObservabilityCustomMetricOptions, ObservabilityLogLevel, ObservabilityMetricType } from '@inditex/core-o11y-js';
import { getObservabilityConnector } from 'src/utils/providers/ITXObservabilityProvider';
import { useObservabilityStore } from 'src/store/useObservability/useObservabilityStore';
import { METHODS_SUMMARY, MethodsSummary, UPLOAD_METHODS, UploadMethod } from './metrics/product-upload/productUploadTypes';

const O11Y_RETRY_DELAY_MS = 200;
const O11Y_MAX_RETRIES = 25;

const executeWithConnector = (
  callback: (connector: NonNullable<ReturnType<typeof getObservabilityConnector>>) => void,
  retryCount = 0,
): void => {
  const connector = getObservabilityConnector();

  if (connector) {
    callback(connector);
    return;
  }

  if (retryCount >= O11Y_MAX_RETRIES) {
    return;
  }

  window.setTimeout(() => {
    executeWithConnector(callback, retryCount + 1);
  }, O11Y_RETRY_DELAY_MS);
};

export type SendLogOptions = {
  message: string;
  level?: ObservabilityLogLevel;
  label: string;
  tags?: Record<string, string>;
};

export type SendMetricOptions = {
  name: string;
  type?: ObservabilityMetricType;
  value: number;
  tags?: Record<string, string>;
};

export const sendMetric = ({ name, type = ObservabilityMetricType.COUNTER, value, tags = {} }: SendMetricOptions): void => {
  const options: ObservabilityCustomMetricOptions = { name, type, value, tags };
  executeWithConnector((connector) => {
    connector.sendCustomMetric(options);
  });
};

export const flushMetrics = (): void => {
  executeWithConnector((connector) => {
    connector.forceFlush();
  });
};

export const getMethodsSummary = (usedMethods: UploadMethod[]): MethodsSummary => {
  const hasBarcode = usedMethods.includes(UPLOAD_METHODS.BARCODE);
  const hasImage = usedMethods.includes(UPLOAD_METHODS.IMAGE_AI);
  if (hasBarcode && hasImage) return METHODS_SUMMARY.BOTH;
  if (hasImage) return METHODS_SUMMARY.IMAGE_ONLY;
  return METHODS_SUMMARY.BARCODE_ONLY;
};

export const sendLog = ({ message, level = ObservabilityLogLevel.INFO, label, tags = {} }: SendLogOptions): void => {
  const uploadId = useObservabilityStore.getState().observabilityData?.uploadId;
  const enrichedTags = uploadId && !tags.upload_id ? { ...tags, upload_id: uploadId } : tags;
  executeWithConnector((connector) => {
    connector.sendLog({ message, level, label, tags: enrichedTags });
  });
};
